import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './utils/api';
import { errorMessage, renderable } from './utils/errorMessage';
// Phase 7 sign-off: profiles email, password, display_name, short_text.
import { MAX_LENGTHS } from './utils/inputValidation';
import { useAuth } from './hooks/useAuth';
import './AdminProfileManager.css';

export default function AdminProfileManager() {
  useAuth(); // ensure authenticated
  const [admins, setAdmins] = useState([]);
  const [, setTotal] = useState(0);
  const [page] = useState(1);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | { mode: 'create'|'edit', item? }
  const [form, setForm] = useState({ email: '', full_name: '', password: '', can_manage_admins: false });
  const [tempPw, setTempPw] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadAdmins = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/v1/admin/profiles?page=${page}&per_page=50`);
      if (res.ok) {
        const data = await res.json();
        setAdmins(data.items || []);
        setTotal(data.total || 0);
      }
    } catch { setError('Failed to load admin profiles'); }
  }, [page]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const filteredAdmins = search
    ? admins.filter(a => a.full_name?.toLowerCase().includes(search.toLowerCase()) || a.email?.toLowerCase().includes(search.toLowerCase()))
    : admins;

  const handleCreate = async () => {
    setError('');
    try {
      const res = await apiFetch('/api/v1/admin/profiles', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setModal(null);
        setMessage('Admin created');
        loadAdmins();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(errorMessage(err, 'Failed to create admin'));
      }
    } catch { setError('Failed to create admin'); }
  };

  const handleUpdate = async () => {
    setError('');
    try {
      const { password, ...fields } = form;
      const res = await apiFetch(`/api/v1/admin/profiles/${modal.item.id}`, {
        method: 'PUT',
        body: JSON.stringify(fields),
      });
      if (res.ok) {
        setModal(null);
        setMessage('Admin updated');
        loadAdmins();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(errorMessage(err, 'Failed to update'));
      }
    } catch { setError('Update failed'); }
  };

  const handleDeactivate = async (a) => {
    if (!window.confirm(`Deactivate ${a.full_name}?`)) return;
    try {
      const res = await apiFetch(`/api/v1/admin/profiles/${a.id}`, { method: 'DELETE' });
      if (res.ok) {
        setMessage('Admin deactivated');
        loadAdmins();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(errorMessage(err, 'Failed to deactivate'));
      }
    } catch { setError('Failed to deactivate'); }
  };

  const handleResetPassword = async (a) => {
    if (!window.confirm(`Reset password for ${a.full_name}?`)) return;
    try {
      const res = await apiFetch(`/api/v1/admin/profiles/${a.id}/reset-password`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setTempPw(data.temporary_password);
      } else { setError('Password reset failed'); }
    } catch { setError('Password reset failed'); }
  };

  return (
    <div className="admin-profiles">
      <div className="ap-header">
        <h1>Site Admin Manager</h1>
        <button className="btn-primary" onClick={() => {
          setForm({ email: '', full_name: '', password: '', can_manage_admins: false });
          setModal({ mode: 'create' });
        }}>+ Add Sys-Admin</button>
      </div>

      {/* `renderable` is belt and braces, not the fix. Every setError above
          goes through errorMessage; this only stops a MISSED call site
          blanking the page, because React 18 throws on an object child and
          this app has no ErrorBoundary. */}
      {error && <div className="ap-error">{renderable(error)} <button onClick={() => setError('')}>×</button></div>}
      {message && <div className="ap-msg">{message} <button onClick={() => setMessage('')}>×</button></div>}
      {tempPw && (
        <div className="ap-msg temp-pw">
          Temporary password: <code>{tempPw}</code>
          <button onClick={() => { navigator.clipboard.writeText(tempPw); setMessage('Copied!'); }}>Copy</button>
          <button onClick={() => setTempPw(null)}>×</button>
        </div>
      )}

      <div className="ap-toolbar">
        <input placeholder="Search sys-admins..." value={search} onChange={e => setSearch(e.target.value)} maxLength={MAX_LENGTHS.short_text} />
      </div>

      <table className="ap-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Root</th>
            <th>Manage Sys-Admins</th>
            {/* There is deliberately no credits column here. T12 dropped
                `credit_balance` from both SELECTs behind GET /admin/profiles
                (list and update) because it is the legacy per-user ledger:
                nothing on the analyse path spends it, so the figure this column
                showed was never the money. Nothing replaced it in the payload
                either: unlike the site-admin USER list -- which was repointed
                at the org pool in the same ticket -- /admin/profiles returns no
                pool balance and no per-period consumption, so there is no
                honest number in this response to print under a "Credits"
                heading. The old cell was `a.credit_balance?.toLocaleString()`,
                which after T12 optional-chained to an empty cell in every row:
                a credit heading on a credit screen with nothing under it, which
                is how the O3 incident started (a value that was not really
                there, presented as a balance). Fix the payload before the
                column: if this screen ever needs the pool, add it to
                /admin/profiles and render null as a dash -- never as 0 -- and
                do not fetch it per row from the client. Credits are one pool
                per organisation and are administered on the org screens. */}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAdmins.map(a => (
            <tr key={a.id}>
              <td className="name-cell">{a.full_name} {a.is_root && <span className="root-badge">ROOT</span>}</td>
              <td>{a.email}</td>
              <td>
                <span className={`status-chip ${a.is_active ? 'active' : 'inactive'}`}>
                  {a.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>{a.is_root ? '✓' : '—'}</td>
              <td>{a.can_manage_admins ? '✓' : '—'}</td>
              <td className="actions-cell">
                {!a.is_root && (
                  <>
                    <button className="btn-sm" onClick={() => {
                      setForm({ email: a.email, full_name: a.full_name, can_manage_admins: a.can_manage_admins });
                      setModal({ mode: 'edit', item: a });
                    }}>Edit</button>
                    <button className="btn-sm" onClick={() => handleResetPassword(a)}>Reset PW</button>
                    {a.is_active && (
                      <button className="btn-sm danger" onClick={() => handleDeactivate(a)}>Deactivate</button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
          {filteredAdmins.length === 0 && (
            // colSpan tracks the header count above: 6 since the credits
            // column was removed (T12). A drifting colSpan here is a known bug
            // pattern in this repo -- recount the <th> list, do not decrement.
            <tr><td colSpan={6} className="empty-cell">No sys-admin profiles found.</td></tr>
          )}
        </tbody>
      </table>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{modal.mode === 'edit' ? 'Edit Sys-Admin' : 'Create Sys-Admin'}</h2>
            <div className="modal-form">
              <label>Full Name
                <input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} maxLength={MAX_LENGTHS.display_name} />
              </label>
              <label>Email
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} maxLength={MAX_LENGTHS.email} />
              </label>
              {modal.mode === 'create' && (
                <label>Password
                  <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} maxLength={MAX_LENGTHS.password} autoComplete="new-password" />
                </label>
              )}
              <label className="checkbox-label">
                <input type="checkbox" checked={form.can_manage_admins}
                  onChange={e => setForm({...form, can_manage_admins: e.target.checked})} />
                Can manage other sys-admins
              </label>
              <div className="btn-row">
                <button className="btn-primary" onClick={modal.mode === 'edit' ? handleUpdate : handleCreate}>
                  {modal.mode === 'edit' ? 'Update' : 'Create'}
                </button>
                <button className="btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The per-admin "Adjust Credits" modal lived here. It POSTed to
          /api/v1/admin/credits/{id}/adjust, which T10 retired to a 410
          (backend/app/api/credits.py) because it wrote users.credit_balance --
          a counter no analysis ever spends, so every "+20 credits" granted from
          this screen was recorded, shown, and unspendable. Post-T10 it was also
          simply broken: the 410 landed in `setError`, which printed the whole
          English retirement sentence into the page error bar, left the modal
          open and never reloaded the list, so it read as a server fault rather
          than a removed feature. Removed together with the column it read
          (T12). The replacement is POST /api/v1/admin/credits/org-grant, which
          grants to an ORGANISATION; as of this change nothing in frontend/src
          calls it, and this screen is not the place for one -- this list is
          staff accounts, and an org grant needs an org picker. Until that UI
          exists a grant is an authenticated API call, so do not restore a
          per-person credit control here to fill the gap. */}
    </div>
  );
}
